import { useCallback, useContext, useMemo } from 'react';
import _ from 'lodash';
import FilterPlatesContext from '../contexts/FilterPlatesContext';
import buildFilterInput from '../utils/buildFilterInput';
import GridContext from '../contexts/GridContext';
import mergeFilterInputs from '../mergeFilterInputs';
import FilterPlateContext from '../contexts/FilterPlateContext';
import CardContext from '../contexts/CardContext';
import visTypeCheckers from '../types/visTypeCheckers';
import EntityDetailsContext from '../screens/EntityDetailsShow/EntityDetailsContext';
import DashboardGadgetContext from '../contexts/DashboardGadgetContext';
import EntityFilterScopeContext from '../contexts/EntityFilterScopeContext';
import PopupContext from '../contexts/PopupContext';
import ReportContext from '../contexts/ReportContext';
import BoardContext from '../contexts/BoardContext';
import isPerformanceBoard from '../isPerformanceBoard';
import MetricOptionsContext from '../contexts/MetricOptionsContext';
import isDefined from '../isDefined';
import metricTypeCheckers from '../types/metricTypeCheckers';
import CompoundMetricsContext from '../contexts/CompoundMetricsContext';
import { DefaultDatasetFiltersContext } from '../contextProviders/SplashScreenProviders/DatasetProviders/DefaultDatasetFiltersProvider';
import { BonusOverviewContext } from '../components/DriverAX/providers/BonusOverviewProvider';
import isCustomerLaneBoard from '../isCustomerLaneBoard';
import filterPlateTypeCheckers from '../types/filterPlateTypeCheckers';

export const usePopupEntityFilter = () => {
  const entityDetails = useContext(EntityDetailsContext);

  const {
    isOpen: isPopupOpen,
    selectedReport: popupReport,
    selectedBoard: popupBoard,
    dashboardGadget: popupFromGadget,
  } = useContext(PopupContext);

  const getPopupEntityFilter = useCallback(() => {
    if (entityDetails && entityDetails.entityFilter) {
      if (
        (popupReport || popupBoard) &&
        isPopupOpen &&
        popupFromGadget &&
        popupFromGadget.isEntityFilterEnabled
      ) {
        // If there is a popup which was triggered by a gadget with the
        // entity filter enabled
        return entityDetails.entityFilter;
      }
    }

    return undefined;
  }, [entityDetails, isPopupOpen, popupBoard, popupFromGadget, popupReport]);

  const popupEntityFilter = useMemo(
    () => getPopupEntityFilter(),
    [getPopupEntityFilter],
  );

  return popupEntityFilter;
};

const useDatasetLevelFilterToApply = (explicitDataTypes?: string[]) => {
  const { board } = useContext(BoardContext);
  const { chartDefinition } = useContext(CardContext);
  const { getCompoundMetricWithMetricDefs } = useContext(
    CompoundMetricsContext,
  );
  const { defaultDatasetFiltersLookup } = useContext(
    DefaultDatasetFiltersContext,
  );
  const { isDefaultFiltersDisabled } = useContext(FilterPlatesContext);
  const { metricOptionsLookup } = useContext(MetricOptionsContext);

  const extractMetrics = useCallback(
    (metricIds: string[]): Metrics.NormalMetric[] => {
      return metricIds
        .map((mid) => metricOptionsLookup[mid])
        .filter(isDefined)
        .reduce((acc, metric) => {
          if (metricTypeCheckers.isSpecialMetric(metric)) {
            return acc;
          }
          if (metricTypeCheckers.isNormalMetric(metric)) {
            return [...acc, metric];
          }

          const compoundWithDefs = getCompoundMetricWithMetricDefs(metric.id);
          if (!compoundWithDefs) {
            return acc;
          }

          return [...acc, ...compoundWithDefs.metrics];
        }, [] as Metrics.NormalMetric[]);
    },
    [getCompoundMetricWithMetricDefs, metricOptionsLookup],
  );

  const usedMetrics = useMemo((): Metrics.NormalMetric[] => {
    if (!chartDefinition) {
      return [];
    }

    if (visTypeCheckers.isV5ChartDef(chartDefinition)) {
      return extractMetrics(chartDefinition.series.map((s) => s.metricId));
    }
    if (visTypeCheckers.isRankingMatrix(chartDefinition)) {
      return extractMetrics(chartDefinition.metrics.map((rm) => rm.metricId));
    }
    if (visTypeCheckers.isSingleMetricDateMatrix(chartDefinition)) {
      return extractMetrics([chartDefinition.metricId]);
    }
    if (visTypeCheckers.isPaceMatrix(chartDefinition)) {
      return extractMetrics([chartDefinition.metricId]);
    }

    return [];
  }, [chartDefinition, extractMetrics]);

  const usedDatasets = useMemo(() => {
    if (usedMetrics.length !== 0) {
      return _.uniq(usedMetrics.map((m) => m.dataType));
    }

    if (board && isPerformanceBoard(board)) {
      return [board.dataType];
    }

    if (explicitDataTypes && explicitDataTypes.length > 0) {
      return explicitDataTypes;
    }

    return [];
  }, [board, explicitDataTypes, usedMetrics]);

  const datasetLevelFilterToApply = useMemo(() => {
    if (isDefaultFiltersDisabled) {
      return undefined;
    }

    if (usedDatasets.length === 1) {
      return defaultDatasetFiltersLookup[usedDatasets[0]];
    }

    return undefined;
  }, [defaultDatasetFiltersLookup, isDefaultFiltersDisabled, usedDatasets]);

  return datasetLevelFilterToApply;
};

/**
 * This is a central point for our visualisations to control filtering
 */
const useFilterInput = ({
  field,
  dataTypes,
  extractEntityFilter,
  entityFilterAsScope,
  ignoreQuickFilters,
}: {
  /**
   * Field to be ignored when building a drilldown from
   * the {@link KeywordPicker} within {@link FilterPlateForm}
   *
   * There appears to be some duplicated of concerns via:
   * chartDefDimensionField within this file. Needs review.
   */
  field?: string;

  /**
   * List of dataTypes to sanitize the final FI against
   */
  dataTypes?: string[];

  /**
   * If set to true:
   * Changes behaviour of the following until another drilldown gets applied:
   *
   * lastDrilldown
   * - This will become a representation of the entity filter
   * filterInput
   * - This will exclude the entityFilter
   */
  extractEntityFilter?: boolean;

  /**
   * If set to true (requires extractEntityFilter to also be true):
   * The entity filter from EntityDetailsContext will be kept in filterInput
   * as a regular filter instead of being extracted as lastDrilldown.
   * Drilldowns are still extractable as lastDrilldown normally.
   */
  entityFilterAsScope?: boolean;

  /**
   * If set to true, the quickFilters from FilterPlatesContext are left out.
   * Used to count what each quick filter would return on its own.
   */
  ignoreQuickFilters?: boolean;
}): {
  filterInput: FilterInput;
  lastDrilldown?: FilterInput;
} => {
  const entityDetails = useContext(EntityDetailsContext);
  const {
    drillDowns: allDrillDownPlates,
    drillDownsAsFixed,
    scopeAsFixed,
    quickFilters,
  } = useContext(FilterPlatesContext);
  const { metricFiltering: popupGridFilter } = useContext(GridContext);
  const { chartDefinition } = useContext(CardContext);
  const { isOpen: isDrillDownOpen } = useContext(FilterPlateContext);
  const { dashboardGadget } = useContext(DashboardGadgetContext);
  // A Studio block previewed as an entity (it has no dashboard gadget)
  const { isEntityFilterEnabled: isScopeEntityFiltered } = useContext(
    EntityFilterScopeContext,
  );
  const { report } = useContext(ReportContext);
  const popupEntityFilter = usePopupEntityFilter();
  const { selectedReport: popupReport } = useContext(PopupContext);
  const { board } = useContext(BoardContext);
  const datasetLevelFilterToApply = useDatasetLevelFilterToApply(dataTypes);
  const { currentMode } = useContext(BonusOverviewContext);

  const chartDefDimensionField = useMemo(() => {
    if (!chartDefinition) {
      return undefined;
    }

    if (visTypeCheckers.isV5ChartDef(chartDefinition)) {
      return chartDefinition.dimensionA && chartDefinition.dimensionA.field;
    }

    if (visTypeCheckers.isGauge(chartDefinition)) {
      return chartDefinition.peerGroup;
    }

    if (visTypeCheckers.isRankingMatrix(chartDefinition)) {
      return chartDefinition.groupByField;
    }

    if (visTypeCheckers.isSingleMetricDateMatrix(chartDefinition)) {
      return chartDefinition.groupByField;
    }

    return undefined;
  }, [chartDefinition]);

  const shouldIgnoreLastForDimension = useMemo(() => {
    // Ignore the last drill-down for a dashboard/report gadget
    // if it is filtering against how the data is grouped
    if (drillDownsAsFixed.length === 0 || chartDefinition === undefined) {
      return false;
    }

    const lastDrillField =
      drillDownsAsFixed[drillDownsAsFixed.length - 1].field;
    const dimensionField = chartDefDimensionField;

    if (
      !!lastDrillField &&
      !!dimensionField &&
      lastDrillField === dimensionField
    ) {
      return true;
    }

    return false;
  }, [drillDownsAsFixed, chartDefinition, chartDefDimensionField]);

  const getScopes = useCallback(() => {
    if (isDrillDownOpen) {
      return [];
    } else {
      return scopeAsFixed;
    }
  }, [isDrillDownOpen, scopeAsFixed]);

  const lastTextDrilldownIndex = useMemo(() => {
    return _.findLastIndex(drillDownsAsFixed, (d) => d.fieldType === 'text');
  }, [drillDownsAsFixed]);

  const drillDowns = useMemo(() => {
    if (extractEntityFilter && lastTextDrilldownIndex >= 0) {
      return drillDownsAsFixed.filter((_, i) => i !== lastTextDrilldownIndex);
    } else if (isDrillDownOpen) {
      return drillDownsAsFixed.filter((d) => d.field !== field);
    } else if (!extractEntityFilter && shouldIgnoreLastForDimension) {
      return drillDownsAsFixed.slice(0, drillDownsAsFixed.length - 1);
    } else {
      return drillDownsAsFixed;
    }
  }, [
    extractEntityFilter,
    lastTextDrilldownIndex,
    isDrillDownOpen,
    field,
    shouldIgnoreLastForDimension,
    drillDownsAsFixed,
  ]);

  const drillDownAndScopeFi = useMemo((): FilterInput => {
    const scopes = getScopes();
    return buildFilterInput({
      scopes,
      drillDowns,
      dataType: dataTypes,
    });
  }, [dataTypes, drillDowns, getScopes]);

  const entityFi = useMemo((): FilterInput | undefined => {
    const isEntityFilteredDashboardGadget =
      (dashboardGadget && dashboardGadget.isEntityFilterEnabled) ||
      isScopeEntityFiltered;
    const isEntityFilteredReport =
      report && report.isEntityFilterEnabled && popupReport === undefined;
    const isEntityFilteredBoard =
      board &&
      (isPerformanceBoard(board) || isCustomerLaneBoard(board)) &&
      board.isEntityFilterEnabled;

    const isExclusionsGrid = !!currentMode?.taggableField;

    const isEntityFilteredContent =
      isEntityFilteredDashboardGadget ||
      isEntityFilteredReport ||
      isExclusionsGrid ||
      isEntityFilteredBoard;

    if (
      entityDetails &&
      entityDetails.entityFilter &&
      isEntityFilteredContent
    ) {
      if (
        extractEntityFilter &&
        !entityFilterAsScope &&
        !drillDownsAsFixed.length &&
        !popupEntityFilter
      ) {
        return undefined;
      }
      return entityDetails.entityFilter;
    }

    return undefined;
  }, [
    board,
    currentMode?.taggableField,
    dashboardGadget,
    drillDownsAsFixed.length,
    entityDetails,
    entityFilterAsScope,
    extractEntityFilter,
    isScopeEntityFiltered,
    popupEntityFilter,
    popupReport,
    report,
  ]);

  const lastDrilldown = useMemo((): FilterInput | undefined => {
    let fixed: FixedFilter | undefined = undefined;
    if (extractEntityFilter) {
      // Only use entity filter if configured in Report/Dashboard gadget
      const isEntityFilterEnabled =
        dashboardGadget?.isEntityFilterEnabled ||
        report?.isEntityFilterEnabled ||
        isScopeEntityFiltered;
      if (
        !entityFilterAsScope &&
        !drillDownsAsFixed.length &&
        entityDetails?.selectedEntity &&
        !popupEntityFilter
      ) {
        fixed = {
          id: 'entity-fixed-filter',
          mode: 'editing',
          fieldType: 'text',
          field: entityDetails.selectedEntity.type.primaryField,
          keywordValues: [entityDetails.selectedEntity.fieldValue],
        };
      } else if (
        !entityFilterAsScope &&
        !drillDownsAsFixed.length &&
        entityDetails?.entityFilter &&
        isEntityFilterEnabled &&
        !popupEntityFilter
      ) {
        return entityDetails.entityFilter;
      } else if (lastTextDrilldownIndex >= 0) {
        fixed = drillDownsAsFixed[lastTextDrilldownIndex];
      }
    } else {
      if (drillDownsAsFixed.length === 0) {
        if (
          entityFi &&
          entityFi.keywords &&
          entityFi.keywords.length > 0 &&
          (dashboardGadget?.isEntityFilterEnabled ||
            report?.isEntityFilterEnabled ||
            isScopeEntityFiltered)
        ) {
          const keywordFilter = entityFi.keywords[0];
          fixed = {
            id: 'entity-casted-fixed-filter',
            mode: 'editing',
            fieldType: 'text',
            field: keywordFilter.field,
            keywordValues: keywordFilter.values,
          };
        }
      } else {
        fixed = drillDownsAsFixed[drillDownsAsFixed.length - 1];
      }
    }
    // Build FI from the derived fixed filter (if any)
    const lastAsFi = fixed
      ? buildFilterInput({ scopes: [], drillDowns: [fixed] })
      : undefined;

    // Extend with entity filter if set
    if (entityFi && !entityFilterAsScope) {
      return lastAsFi ? mergeFilterInputs(lastAsFi, entityFi) : entityFi;
    }

    return lastAsFi;
  }, [
    entityFilterAsScope,
    extractEntityFilter,
    entityFi,
    dashboardGadget?.isEntityFilterEnabled,
    report?.isEntityFilterEnabled,
    isScopeEntityFiltered,
    drillDownsAsFixed,
    entityDetails?.selectedEntity,
    entityDetails?.entityFilter,
    popupEntityFilter,
    lastTextDrilldownIndex,
  ]);

  const excludeTermedDrivers = useMemo(
    () =>
      allDrillDownPlates.some(filterPlateTypeCheckers.isExcludeTermedDrivers),
    [allDrillDownPlates],
  );

  /**
   * Note that there is additional filtering which occurs on the metric
   * level
   *
   * {@link useToMetricInput}
   */
  const filterInput = useMemo((): FilterInput => {
    const filtersToMerge: FilterInput[] = [];

    if (entityFi) {
      filtersToMerge.push(entityFi);
    }

    if (popupGridFilter) {
      filtersToMerge.push(popupGridFilter);
    }

    if (popupEntityFilter) {
      // If there is a popup which was triggered by a gadget with the
      // entity filter enabled
      filtersToMerge.push(popupEntityFilter);
    }

    if (datasetLevelFilterToApply) {
      filtersToMerge.push(datasetLevelFilterToApply.filterInput);
    }

    if (!ignoreQuickFilters) {
      quickFilters.forEach((quickFilter) => {
        filtersToMerge.push(quickFilter);
      });
    }

    if (excludeTermedDrivers) {
      filtersToMerge.push({ excludeTermedDrivers: true });
    }

    return filtersToMerge.reduce(
      (a, b) => {
        return mergeFilterInputs(a, b);
      },
      { ...drillDownAndScopeFi },
    );
  }, [
    datasetLevelFilterToApply,
    drillDownAndScopeFi,
    entityFi,
    excludeTermedDrivers,
    ignoreQuickFilters,
    popupEntityFilter,
    popupGridFilter,
    quickFilters,
  ]);

  return {
    filterInput,
    lastDrilldown,
  };
};

export default useFilterInput;
